import type { Background } from '../content/types.ts';
import { CUSTOM_CONTENT_ID_PREFIX, isCustomContentId } from './customContentId.ts';

/**
 * The player's own Backgrounds.
 *
 * A custom Background is an ordinary `Background`: an entry on one of the
 * printed tables, offering Skills to choose between at that table's Rank. It is
 * not a new kind of thing the creator has to know about.
 *
 * Definitions live here, apart from any crawler. A crawler that took a custom
 * Background stores its ID in the same creation record it uses for a printed
 * one, so two crawlers share one definition.
 *
 * RULE DCC-CUSTOM-001 applies here too: the ID is assigned once and never
 * changes, including when the name is edited.
 */

export const CUSTOM_BACKGROUND_LIBRARY_SCHEMA_VERSION = 1;

export interface CustomBackgroundLibrary {
  kind: 'custom-background-library';
  schemaVersion: number;
  backgrounds: Background[];
  createdAt: string;
  updatedAt: string;
}

export function createCustomBackgroundLibrary(now: string): CustomBackgroundLibrary {
  return {
    kind: 'custom-background-library',
    schemaVersion: CUSTOM_BACKGROUND_LIBRARY_SCHEMA_VERSION,
    backgrounds: [],
    createdAt: now,
    updatedAt: now,
  };
}

export interface LibraryValidation {
  valid: boolean;
  problems: string[];
}

export const MAX_CUSTOM_BACKGROUND_NAME_LENGTH = 60;

/**
 * Checks a stored library before the application trusts it.
 *
 * Same bar as the custom Skill library: not "does this resemble a Background"
 * but "can every consumer handle this". A Background reaches the creator and
 * the starting-Skill calculation through here and nowhere else.
 *
 * The table it belongs to is NOT checked here, because the domain layer has no
 * catalogue. Composition drops a Background whose table does not exist, so an
 * orphan cannot reach the creator either way.
 */
export function validateCustomBackgroundLibrary(raw: unknown): LibraryValidation {
  const problems: string[] = [];
  const push = (m: string) => problems.push(m);

  if (typeof raw !== 'object' || raw === null) {
    return { valid: false, problems: ['The custom Background library is not an object.'] };
  }
  const lib = raw as Record<string, unknown>;

  if (lib['kind'] !== 'custom-background-library') {
    push('kind must be "custom-background-library".');
  }
  if (typeof lib['schemaVersion'] !== 'number') push('schemaVersion must be a number.');
  if (typeof lib['createdAt'] !== 'string') push('createdAt must be a string.');
  if (typeof lib['updatedAt'] !== 'string') push('updatedAt must be a string.');

  const backgrounds = lib['backgrounds'];
  if (!Array.isArray(backgrounds)) {
    push('backgrounds must be a list.');
    return { valid: problems.length === 0, problems };
  }

  const seen = new Set<string>();
  backgrounds.forEach((entry, i) => checkBackground(entry, i, seen, push));

  return { valid: problems.length === 0, problems };
}

function checkBackground(
  entry: unknown,
  i: number,
  seen: Set<string>,
  push: (message: string) => void,
): void {
  const at = `backgrounds[${i}]`;
  if (typeof entry !== 'object' || entry === null) {
    push(`${at} is not an object.`);
    return;
  }
  const background = entry as Record<string, unknown>;

  const id = background['id'];
  if (typeof id !== 'string' || id === '') {
    push(`${at} has no id.`);
  } else if (!isCustomContentId(id)) {
    push(`${at} has id "${id}", which is not in the custom namespace.`);
  } else if (id === CUSTOM_CONTENT_ID_PREFIX) {
    push(`${at} has an id that is only the prefix.`);
  } else if (seen.has(id)) {
    push(`${at} repeats the id "${id}".`);
  } else {
    seen.add(id);
  }

  const name = background['name'];
  if (typeof name !== 'string' || name.trim() === '') {
    push(`${at} has no name.`);
  } else if (name.length > MAX_CUSTOM_BACKGROUND_NAME_LENGTH) {
    push(`${at} name is longer than ${MAX_CUSTOM_BACKGROUND_NAME_LENGTH} characters.`);
  }

  const tableId = background['tableId'];
  if (typeof tableId !== 'string' || tableId === '') {
    push(`${at} does not say which table it belongs to.`);
  }

  // A custom Background is chosen directly, never rolled for, so it must not
  // claim a face of the table's die.
  if (background['roll'] !== null) {
    push(`${at} must not sit on a face of the table's die.`);
  }

  const skillIds = background['skillIds'];
  if (!Array.isArray(skillIds)) {
    push(`${at} skillIds must be a list.`);
  } else if (skillIds.length === 0) {
    push(`${at} offers no Skills to choose between.`);
  } else if (skillIds.some((s) => typeof s !== 'string' || s === '')) {
    push(`${at} has a Skill entry that is not an ID.`);
  } else if (new Set(skillIds).size !== skillIds.length) {
    push(`${at} offers the same Skill more than once.`);
  }

  // Starting equipment is real now. The IDs themselves cannot be checked here -
  // the domain layer has no catalogue - so composition drops a Background whose
  // Items do not resolve, the same way it drops one whose Skills do not
  // (RULE DCC-CUSTOM-009). What IS checked here is the shape.
  const equipment = background['startingEquipment'];
  if (equipment !== undefined) {
    if (!Array.isArray(equipment)) {
      push(`${at} startingEquipment must be a list.`);
    } else {
      const seenItems = new Set<string>();
      equipment.forEach((grant, g) => {
        const where = `${at} startingEquipment[${g}]`;
        if (typeof grant !== 'object' || grant === null) {
          push(`${where} is not an object.`);
          return;
        }
        const entry = grant as Record<string, unknown>;
        const itemId = entry['itemId'];
        if (typeof itemId !== 'string' || itemId === '') {
          push(`${where} has no item ID.`);
        } else if (seenItems.has(itemId)) {
          // One entry per Item: two rows for one ID is two answers to "how
          // many does this grant".
          push(`${where} repeats the Item "${itemId}".`);
        } else {
          seenItems.add(itemId);
        }
        const quantity = entry['quantity'];
        if (!Number.isInteger(quantity) || (quantity as number) < 1) {
          push(`${where} must grant a whole quantity of at least 1.`);
        }
      });
    }
  }

  const source = background['source'];
  if (typeof source !== 'object' || source === null) {
    push(`${at} has no source.`);
    return;
  }
  const src = source as Record<string, unknown>;
  if (src['book'] !== 'custom') {
    push(`${at} must have a custom source.`);
  }
  if (typeof src['createdAt'] !== 'string' || src['createdAt'].trim() === '') {
    push(`${at} must record when it was created.`);
  }
}

type LibraryMigration = (record: Record<string, unknown>) => Record<string, unknown>;

/** None yet; the map exists so version 2 is a one-line change. */
export const CUSTOM_BACKGROUND_LIBRARY_MIGRATIONS: Record<number, LibraryMigration> = {};

export function migrateCustomBackgroundLibraryRecord(
  raw: unknown,
): Record<string, unknown> | null {
  if (typeof raw !== 'object' || raw === null) return null;
  let record = { ...(raw as Record<string, unknown>) };

  const version = record['schemaVersion'];
  if (typeof version !== 'number') return null;
  if (version > CUSTOM_BACKGROUND_LIBRARY_SCHEMA_VERSION) return null;

  let current = version;
  while (current < CUSTOM_BACKGROUND_LIBRARY_SCHEMA_VERSION) {
    const step = CUSTOM_BACKGROUND_LIBRARY_MIGRATIONS[current];
    if (!step) return null;
    record = step(record);
    current += 1;
    record['schemaVersion'] = current;
  }
  return record;
}
